import { z } from "zod";
import { changesOf } from "./zod-util";
import type { ComplianceStatus, DocState } from "./compliance";
import type { UserRef } from "./api-types";

/**
 * Work orders (spec §6): a sub's or crew's job: scope, materials, optional labor lines from the
 * labor price list, optional start and end. Amounts in cents; quantities in thousandths, so
 * 32.5 squares is 32500.
 */

export const WORK_ORDER_STATUSES = ["draft", "sent", "confirmed", "declined", "proposed", "completed", "canceled"] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  draft: "Draft",
  sent: "Sent",
  confirmed: "Confirmed",
  declined: "Declined",
  proposed: "New time proposed",
  completed: "Completed",
  canceled: "Canceled",
};

/** Largest quantity on a line: 100,000 units. */
export const MAX_QUANTITY_THOUSANDTHS = 100_000_000;
/** Largest price per unit: $100,000. */
export const MAX_UNIT_CENTS = 10_000_000;

export interface WorkOrderLine {
  laborItemId: string | null;
  description: string;
  unit: string;
  /** Thousandths: 1 unit = 1000. */
  quantity: number;
  unitCents: number;
  totalCents: number;
}

/** quantity × price, rounded to the cent (half up). Both are whole numbers, so this is exact before rounding. */
export function lineTotalCents(quantityThousandths: number, unitCents: number): number {
  if (!Number.isInteger(quantityThousandths) || !Number.isInteger(unitCents) || quantityThousandths < 0 || unitCents < 0) {
    throw new Error("Quantity and price have to be whole, non-negative numbers.");
  }
  const product = quantityThousandths * unitCents;
  return Math.floor(product / 1000) + (product % 1000 >= 500 ? 1 : 0);
}

/** Recomputes every line's total from its quantity and price, and the work order's total. */
export function priceLines(lines: Omit<WorkOrderLine, "totalCents">[]): { lines: WorkOrderLine[]; totalCents: number } {
  const priced = lines.map((l) => ({ ...l, totalCents: lineTotalCents(l.quantity, l.unitCents) }));
  return { lines: priced, totalCents: priced.reduce((s, l) => s + l.totalCents, 0) };
}

/** "32.5" or "32,5" → 32500; null when it isn't a number with at most three decimals. */
export function parseQuantity(text: string): number | null {
  const t = text.trim().replace(",", ".");
  if (!/^\d{1,6}(\.\d{0,3})?$/.test(t)) return null;
  const [whole, frac = ""] = t.split(".");
  return Number(whole) * 1000 + Number(frac.padEnd(3, "0"));
}

export function formatQuantity(thousandths: number): string {
  const whole = Math.floor(thousandths / 1000);
  const frac = String(thousandths % 1000).padStart(3, "0").replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : String(whole);
}

/** No answer from the sub this many hours after sending: flag it. */
export function awaitingTooLong(status: WorkOrderStatus, sentAt: string | null, now: Date, hours: number): boolean {
  return status === "sent" && !!sentAt && now.getTime() - new Date(sentAt).getTime() > hours * 3_600_000;
}

// ── API shapes ───────────────────────────────────────────────────────────

/** What a labor item is counted by, from a measurement report (or typed in). */
export const LABOR_BASES = ["manual", "squares", "steep8", "steep10", "hip_ridge", "eave_rake", "valleys", "step_flashing"] as const;
export type LaborBasis = (typeof LABOR_BASES)[number];
export const LABOR_BASIS_LABELS: Record<LaborBasis, string> = {
  manual: "Typed in",
  squares: "Squares (with waste)",
  steep8: "Squares at 8/12–9/12",
  steep10: "Squares at 10/12 and up",
  hip_ridge: "Hips + ridges (ft)",
  eave_rake: "Eaves + rakes (ft)",
  valleys: "Valleys (ft)",
  step_flashing: "Step flashing (ft)",
};

export interface LaborItem {
  id: string;
  name: string;
  unit: string;
  basis: LaborBasis;
  priceCents: number;
  sort: number;
  archived: boolean;
}

export const laborItemInputSchema = z.object({
  name: z.string().trim().min(1, "Name it").max(120),
  unit: z.string().trim().min(1).max(20).default("SQ"),
  basis: z.enum(LABOR_BASES).default("manual"),
  priceCents: z.number().int().min(0).max(MAX_UNIT_CENTS),
});

export const laborItemUpdateSchema = changesOf(laborItemInputSchema).extend({ archived: z.boolean().optional() });
export const laborItemOrderSchema = z.object({ ids: z.array(z.string().uuid()).max(500) });

export interface WorkOrderListItem {
  id: string;
  number: number;
  jobId: string;
  jobNumber: number;
  jobLabel: string;
  sub: { id: string; name: string; phone: string; email: string } | null;
  status: WorkOrderStatus;
  title: string;
  /** Null without "See job money". */
  totalCents: number | null;
  startsAt: string | null;
  endsAt: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  /** Sent and no answer within the response window. */
  noResponse: boolean;
  createdAt: string;
}

export interface WorkOrderDetail extends WorkOrderListItem {
  scope: string;
  materials: string;
  /** Empty without "See job money". */
  lines: WorkOrderLine[];
  responseNote: string;
  proposedStartsAt: string | null;
  proposedEndsAt: string | null;
  completedAt: string | null;
  /** The material order it was made from ("Make work order"). */
  fromMaterialOrder: { id: string; number: number } | null;
  complianceOverride: { reason: string; problems: string[]; by: string; at: string } | null;
  /** The sub's paperwork right now. */
  compliance: ComplianceStatus | null;
  address: string;
  pdfUrl: string | null;
  appointmentId: string | null;
  createdBy: UserRef | null;
  /** Only right after sending or making a new link. */
  subUrl?: string;
}

const dateTime = z.string().datetime({ offset: true });

const lineSchema = z.object({
  laborItemId: z.string().uuid().nullable().default(null),
  description: z.string().trim().min(1, "Describe the line").max(300),
  unit: z.string().trim().max(20).default(""),
  quantity: z.number().int().min(0).max(MAX_QUANTITY_THOUSANDTHS),
  unitCents: z.number().int().min(0).max(MAX_UNIT_CENTS),
});

const workOrderFields = {
  subId: z.string().uuid().nullable(),
  title: z.string().trim().max(200),
  scope: z.string().trim().max(10_000),
  materials: z.string().trim().max(10_000),
  lines: z.array(lineSchema).max(100),
  startsAt: dateTime.nullable(),
  endsAt: dateTime.nullable(),
};

const endsAfterStart = (w: { startsAt?: string | null; endsAt?: string | null }) => !w.startsAt || !w.endsAt || new Date(w.endsAt) >= new Date(w.startsAt);

export const workOrderInputSchema = z
  .object({
    subId: workOrderFields.subId.default(null),
    title: workOrderFields.title.default(""),
    scope: workOrderFields.scope.default(""),
    materials: workOrderFields.materials.default(""),
    lines: workOrderFields.lines.default([]),
    startsAt: workOrderFields.startsAt.default(null),
    endsAt: workOrderFields.endsAt.default(null),
  })
  .refine(endsAfterStart, { message: "It has to end after it starts", path: ["endsAt"] });

export const workOrderUpdateSchema = z.object(workOrderFields).partial().refine(endsAfterStart, { message: "It has to end after it starts", path: ["endsAt"] });

export const workOrderSendSchema = z.object({
  /** Required when the sub's paperwork is missing or expired (logged). */
  overrideReason: z.string().trim().max(1000).optional(),
});

/** What the sub sees from their link. */
export interface PublicWorkOrder {
  number: number;
  status: WorkOrderStatus;
  company: { name: string; phone: string; email: string; logoUrl: string | null; primaryColor: string };
  subName: string;
  title: string;
  scope: string;
  materials: string;
  address: string;
  lines: WorkOrderLine[];
  totalCents: number;
  startsAt: string | null;
  endsAt: string | null;
  proposedStartsAt: string | null;
  proposedEndsAt: string | null;
  responseNote: string;
  pdfUrl: string | null;
  contactName: string;
}

/** The sub's portal: their work orders (open, and done in the last 90 days) and what their paperwork needs. */
export interface PublicSubPortal {
  company: { name: string; phone: string; email: string; logoUrl: string | null; primaryColor: string };
  subName: string;
  workOrders: (PublicWorkOrder & { id: string })[];
  paperwork: string[];
}

export const SUB_UPLOAD_KINDS = ["photo", "invoice", "certificate", "w9"] as const;
export type SubUploadKind = (typeof SUB_UPLOAD_KINDS)[number];
export const subUploadRequestSchema = z.object({
  kind: z.enum(SUB_UPLOAD_KINDS),
  /** Photos and invoices are for one of their work orders; certificates are for them. */
  workOrderId: z.string().uuid().nullable().default(null),
  files: z.array(z.object({ name: z.string().trim().min(1).max(255), contentType: z.string().trim().min(1).max(120), size: z.number().int().min(1).max(50 * 1024 * 1024) })).min(1).max(20),
});
export const subUploadSchema = z.object({
  kind: z.enum(SUB_UPLOAD_KINDS),
  workOrderId: z.string().uuid().nullable().default(null),
  key: z.string().min(1).max(500),
  name: z.string().trim().min(1).max(255),
  contentType: z.string().trim().min(1).max(120),
  note: z.string().trim().max(500).default(""),
});

export const workOrderRespondSchema = z.discriminatedUnion("answer", [
  z.object({ answer: z.literal("confirm"), note: z.string().trim().max(2000).default("") }),
  z.object({ answer: z.literal("decline"), note: z.string().trim().max(2000).default("") }),
  z
    .object({ answer: z.literal("propose"), startsAt: dateTime, endsAt: dateTime, note: z.string().trim().max(2000).default("") })
    .refine(endsAfterStart, { message: "It has to end after it starts", path: ["endsAt"] }),
]);

// ── Subcontractor paperwork ──────────────────────────────────────────────

export interface ComplianceDoc {
  fileId: string | null;
  fileName: string | null;
  url: string | null;
  expiresOn: string | null;
  state: DocState;
}

export interface ComplianceDetail {
  contactId: string;
  wcKind: "policy" | "exemption" | null;
  workersComp: ComplianceDoc;
  generalLiability: ComplianceDoc;
  w9: ComplianceDoc;
  /** "•••• 1234"; the full number only with "See tax IDs". */
  taxIdMasked: string | null;
  taxId: string | null;
  is1099: boolean;
  wcWaived: boolean;
  glWaived: boolean;
  w9Waived: boolean;
  /** JobNimbus says the W-9 is on file, though no document is tagged here. */
  w9OnFile: boolean;
  status: ComplianceStatus;
}

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date");

export const complianceUpdateSchema = z.object({
  wcKind: z.enum(["policy", "exemption"]).nullable().optional(),
  wcFileId: z.string().uuid().nullable().optional(),
  wcExpiresOn: dateOnly.nullable().optional(),
  glFileId: z.string().uuid().nullable().optional(),
  glExpiresOn: dateOnly.nullable().optional(),
  w9FileId: z.string().uuid().nullable().optional(),
  /** SSN or EIN, 9 digits, kept as typed (12-3456789 or 123-45-6789); "" clears it. */
  taxId: z
    .string()
    .trim()
    .refine((s) => s === "" || (/^[\d\s-]+$/.test(s) && s.replace(/\D/g, "").length === 9), "A tax ID is 9 digits")
    .optional(),
  is1099: z.boolean().optional(),
  wcWaived: z.boolean().optional(),
  glWaived: z.boolean().optional(),
  w9Waived: z.boolean().optional(),
});
